import PropTypes from 'prop-types';
import React, {useCallback} from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import styles from './project-json.css';

const messages = defineMessages({
    title: {id: 'gui.projectJson.title', defaultMessage: 'Code comparison', description: 'Code panel heading'},
    python: {id: 'gui.projectJson.python', defaultMessage: 'Python', description: 'Python teaching view tab'},
    json: {id: 'gui.projectJson.json', defaultMessage: 'JSON', description: 'Native JSON view tab'},
    pythonContent: {
        id: 'gui.projectJson.pythonContent', defaultMessage: 'Read-only Python', description: 'Python text area'
    },
    scope: {id: 'gui.projectJson.scope', defaultMessage: 'View scope', description: 'JSON scope selector'},
    target: {
        id: 'gui.projectJson.target', defaultMessage: 'Current sprite / stage', description: 'Current target JSON'
    },
    project: {id: 'gui.projectJson.project', defaultMessage: 'Entire project', description: 'Full project JSON'},
    close: {id: 'gui.projectJson.close', defaultMessage: 'Close code comparison', description: 'Close code panel'},
    content: {id: 'gui.projectJson.content', defaultMessage: 'Read-only JSON', description: 'JSON text area label'},
    copied: {id: 'gui.projectJson.copied', defaultMessage: 'Copied', description: 'JSON copy success'},
    copyFailed: {
        id: 'gui.projectJson.copyFailed',
        defaultMessage: 'Cannot access the clipboard. Select the text to copy it, or download the code.',
        description: 'JSON copy failure with alternative actions'
    }
});

const stopKeyboardPropagation = event => event.stopPropagation();

const ProjectJsonPanel = ({
    scope, text, name, failed, copyStatus, language, warnings,
    onScopeChange, onLanguageChange, onRefresh, onCopy, onDownload, onClose
}) => {
    const intl = useIntl();
    const changeScope = useCallback(event => onScopeChange(event.target.value), [onScopeChange]);
    const changeLanguage = useCallback(event => onLanguageChange(event.currentTarget.value), [onLanguageChange]);
    return (
        <section
            aria-label={intl.formatMessage(messages.title)}
            className={styles.panel}
            id="project-json-panel"
            onKeyDown={stopKeyboardPropagation}
        >
            <div className={styles.heading}>
                <div className={styles.languages}>
                    {['python', 'json'].map(value => (
                        <button
                            aria-pressed={language === value}
                            key={value}
                            type="button"
                            value={value}
                            onClick={changeLanguage}
                        >{intl.formatMessage(messages[value])}</button>
                    ))}
                </div>
                <button
                    aria-label={intl.formatMessage(messages.close)}
                    type="button"
                    onClick={onClose}
                >{'×'}</button>
            </div>
            <select
                aria-label={intl.formatMessage(messages.scope)}
                value={scope}
                onChange={changeScope}
            >
                <option value="target">{intl.formatMessage(messages.target)}{name ? ` · ${name}` : ''}</option>
                <option value="project">{intl.formatMessage(messages.project)}</option>
            </select>
            {name && <div className={styles.targetName}>{name}</div>}
            {language === 'json' && <p className={styles.hint}>
                <FormattedMessage
                    defaultMessage={'Read-only, automatically updated. opcode identifies a block; ' +
                        'next links the next block; inputs contains parameters and branches.'}
                    description="Explain native Scratch JSON fields"
                    id="gui.projectJson.hint"
                />
            </p>}
            <div className={styles.actions}>
                <button
                    type="button"
                    onClick={onRefresh}
                >
                    <FormattedMessage
                        defaultMessage="Refresh"
                        id="gui.projectJson.refresh"
                    />
                </button>
                <button
                    disabled={!text}
                    type="button"
                    onClick={onCopy}
                >
                    <FormattedMessage
                        defaultMessage="Copy"
                        id="gui.projectJson.copy"
                    />
                </button>
                <button
                    disabled={!text}
                    type="button"
                    onClick={onDownload}
                >
                    <FormattedMessage
                        defaultMessage="Download {format}"
                        id="gui.projectJson.download"
                        values={{format: language === 'python' ? 'Python' : 'JSON'}}
                    />
                </button>
            </div>
            {failed ? (
                <p
                    className={styles.error}
                    role="alert"
                >
                    <FormattedMessage
                        defaultMessage="Could not generate code. Try refreshing the view."
                        id="gui.projectJson.error"
                    />
                </p>
            ) : (
                <textarea
                    readOnly
                    spellCheck={false}
                    aria-label={intl.formatMessage(language === 'python' ? messages.pythonContent : messages.content)}
                    className={styles.code}
                    value={text}
                    wrap="off"
                />
            )}
            <div
                className={styles.status}
                role="status"
            >
                {language === 'python' && <div><FormattedMessage
                    defaultMessage={'Teaching view only, not directly runnable or flashable. ' +
                        'Illustrative APIs require a runtime. Unsupported blocks: {count} (marked in code).'}
                    id="gui.projectJson.pythonHint"
                    values={{count: warnings}}
                /></div>}
                {copyStatus ? intl.formatMessage(messages[copyStatus]) : language === 'json' && <FormattedMessage
                    defaultMessage={'JSON does not include image or sound files. ' +
                        'Save as .sb3 to keep a complete project.'}
                    description="Explain the limits of JSON export"
                    id="gui.projectJson.assetsHint"
                />}
            </div>
        </section>
    );
};

ProjectJsonPanel.propTypes = {
    copyStatus: PropTypes.string.isRequired,
    failed: PropTypes.bool.isRequired,
    language: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
    onDownload: PropTypes.func.isRequired,
    onLanguageChange: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired,
    onScopeChange: PropTypes.func.isRequired,
    scope: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    warnings: PropTypes.number.isRequired
};

export default ProjectJsonPanel;
